import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";

// API
import ContentService from "../../../services/content.service";
import LocalStorageService from "../../../services/localstorage.service";

// Utils
import { AppName } from "../../../common/utils/constants";

// Context
import { StateContext } from "../../../common/context/StateContext";

// MUI
import Grid from "@mui/material/Grid";

// Components
import HeroBanner from "../../../common/components/HeroBanner";
import Reviews from "../components/Reviews";
import FAQ from "../components/FAQ";
import Newsletter from "../components/Newsletter";
import Insititutions from "../components/Insititutions";
import ContentBlock from "./components/ContentBlock";
import ImageBackgroundBlock from "./components/ImageBackgroundBlock";
import Partners from "../components/Partners";
import FreemiumModal from "./components/FreemiumModal";
import ShopModal from "./components/ShopModal";

// Images
import Hero from "../../../images/banners/schools.jpg";
import Packages from "../../../images/banners/block_school_packages.jpg";
import Streams from "../../../images/banners/block_robotics_kits.jpg";
import TeacherTraining from "../../../images/banners/block_teacher_training.jpg";
import SteamDays from "../../../images/banners/block_steam_days.jpg";

const Schools = () => {
    const navigate = useNavigate();
    const { setNotificationMessage, setIsContactFormOpen } =
        useContext(StateContext);

    const [faqList, setFaqList] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);

    useEffect(() => {
        getFaqs();
    }, []);

    useEffect(() => {
        if (!LocalStorageService.getViewedModal()) {
            setIsModalOpen(true);
            LocalStorageService.setViewedModal();
        }
    }, [LocalStorageService]);

    const getFaqs = async () => {
        const { status, data } = await ContentService.getFaqs(
            "is_for_schools=true"
        );

        if (status) {
            setFaqList(data);
        } else {
            setNotificationMessage({
                message: "Could not load FAQ list.",
                type: "error",
            });
        }
    };

    return (
        <>
            <Helmet>
                <title>For Schools {AppName}</title>
                <meta
                    name="description"
                    content="Get ready to join the EdTech revolution! Partnering with Resolute Education provides schools with the ability to offer high-quality coding and robotics content, with minimal wait time, and ongoing support."
                />
                <meta name="robots" content="index" />
            </Helmet>
            <HeroBanner
                lighttheme={true}
                title="Transform your school into an EdTech super hub."
                titleWidth="850px"
                subtitle="Get ready to join the EdTech revolution! Partnering with Resolute Education provides schools with the ability to offer high-quality coding and robotics content, with minimal wait time, and ongoing support."
                subtitleWidth="670px"
                cta="Find Out More"
                image={Hero}
                onClick={() => setIsContactFormOpen(true)}
            />

            {/* Packages */}
            <Grid container id="school-packages">
                <Grid item xs={12} md={6} order={{ xs: 1, md: 1 }}>
                    <ImageBackgroundBlock image={Packages} />
                </Grid>
                <Grid item xs={12} md={6} order={{ xs: 1, md: 2 }}>
                    <ContentBlock
                        title="School Packages"
                        subheader="Your turn-key solution"
                        description="At Resolute we understand that no two schools are the same, and that is why our unique license packages have been developed to cater for every unique use case. From the school that is well adjusted to the demands of EdTech to those just starting out, we have a solution for you!"
                        actionButtonTitle="View Our Packages"
                        onActionBtnClick={() => navigate("/schools/packages")}
                    />
                </Grid>
            </Grid>

            {/* Streams / Robotics Kits */}
            <Grid container id="robotics-kits">
                <Grid item xs={12} md={6} order={{ xs: 2, md: 1 }}>
                    <ContentBlock
                        title="Robotic Kits"
                        subheader="A practical approach to a theoretical world"
                        description="The kits form a critical part of a student’s practical learning experience. We have designed our kits in a modular fashion in order to make it cost-effective, easy to schedule and implement in schools, and reusable over several years."
                        actionButtonTitle="View Our Streams"
                        onActionBtnClick={() => navigate("/schools/streams")}
                    />
                </Grid>
                <Grid item xs={12} md={6} order={{ xs: 1, md: 2 }}>
                    <ImageBackgroundBlock image={Streams} />
                </Grid>
            </Grid>

            {/* Teacher Training */}
            <Grid container id="professional-development">
                <Grid item xs={12} md={6} order={{ xs: 1, md: 1 }}>
                    <ImageBackgroundBlock image={TeacherTraining} />
                </Grid>
                <Grid item xs={12} md={6} order={{ xs: 1, md: 1 }}>
                    <ContentBlock
                        title="Professional Development"
                        subheader="Be prepared for the classrooms of tomorrow"
                        description="At Resolute Education, we are dedicated to equipping teachers with the skills and resources needed to transform their classrooms with innovative robotics and coding.. Our mission is to support educators in creating dynamic and engaging learning experiences that prepare students for the future."
                        actionButtonTitle="What We Cover"
                        onActionBtnClick={() =>
                            navigate("/schools/professional-development")
                        }
                    />
                </Grid>
            </Grid>

            {/* STEAM */}
            <Grid container id="steam-days">
                <Grid item xs={12} md={6} order={{ xs: 2, md: 1 }}>
                    <ContentBlock
                        title="STEAM Days"
                        subheader="A day of discovery"
                        description="Resolute Robotics hosts STEAM innovation days as a part of our initiative to empower students through project-based learning in Science, Technology, Engineering, Arts and Maths. STEAM days aim to expose our learners to the exciting world of robotics and coding while encouraging them to think logically, creatively, and solve problems through team work."
                        actionButtonTitle="What You Can Expect"
                        onActionBtnClick={() => navigate("/schools/steam-days")}
                    />
                </Grid>
                <Grid item xs={12} md={6} order={{ xs: 1, md: 2 }}>
                    <ImageBackgroundBlock image={SteamDays} />
                </Grid>
            </Grid>

            <Reviews />

            <FAQ questions={faqList} />

            <Insititutions />
            <Partners />
            <Newsletter />
            {/* <Contact /> */}

            <ShopModal
                open={isModalOpen}
                handleClose={() => setIsModalOpen(false)}
            />
        </>
    );
};

export default Schools;
